/**
 * Session Users Widget (Owner/Admin)
 * - Shows active/recent guests for the current session
 * - Allows approve/remove/reapprove and manual add
 * - Includes session expiry + extend controls
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import type { SessionUserSummary } from "@jamroom/shared";
import { useSessionStore } from "../store/sessionStore";
import JamroomIconButton from "./JamroomIconButton";
import "./SessionUsersWidget.css";

export interface SessionUsersWidgetProps {
  users: SessionUserSummary[];
  busyKey: string | null;
  onAction: (displayName: string, action: "approve" | "remove" | "reapprove" | "add") => Promise<void> | void;
  onExtendSession: (hoursToAdd: number) => Promise<void> | void;
  placement?: "floating" | "dock";
}

function formatDurationMs(ms: number): string {
  const clamped = Math.max(0, Math.floor(ms));
  const totalSec = Math.floor(clamped / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, "0")}m`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

const SessionUsersWidget: React.FC<SessionUsersWidgetProps> = ({
  users,
  busyKey,
  onAction,
  onExtendSession,
  placement = "floating",
}) => {
  const { createdAt, expiresAt, serverTimeOffsetMs, sessionExpired } = useSessionStore();
  const [open, setOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [extendHours, setExtendHours] = useState(1);
  const [attention, setAttention] = useState(false);
  const [tick, setTick] = useState(0);
  const prevConnectedRef = useRef(0);
  const attentionTimeoutRef = useRef<number | null>(null);

  const connectedCount = useMemo(() => users.filter((u) => u.connected).length, [users]);
  const totalCount = users.length;

  const timing = useMemo(() => {
    const createdAtMs = createdAt ? Date.parse(createdAt) : NaN;
    const expiresAtMs = expiresAt ? Date.parse(expiresAt) : NaN;
    const nowMs = Date.now() + (serverTimeOffsetMs || 0) + tick * 0;
    const totalMs =
      Number.isFinite(createdAtMs) && Number.isFinite(expiresAtMs) ? Math.max(0, expiresAtMs - createdAtMs) : null;
    const remainingMs = Number.isFinite(expiresAtMs) ? Math.max(0, expiresAtMs - nowMs) : null;
    return {
      totalMs,
      remainingMs,
      expiresText: expiresAt && Number.isFinite(expiresAtMs) ? new Date(expiresAtMs).toLocaleString() : null,
    };
  }, [createdAt, expiresAt, serverTimeOffsetMs, tick]);

  useEffect(() => {
    if (!open) return;
    if (!expiresAt) return;
    const id = window.setInterval(() => setTick((v) => v + 1), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt, open]);

  useEffect(() => {
    const prev = prevConnectedRef.current;
    const next = connectedCount;
    prevConnectedRef.current = next;

    if (next > prev && !open) {
      setAttention(true);
      if (attentionTimeoutRef.current !== null) {
        window.clearTimeout(attentionTimeoutRef.current);
      }
      attentionTimeoutRef.current = window.setTimeout(() => {
        attentionTimeoutRef.current = null;
        setAttention(false);
      }, 3500);
    }
  }, [connectedCount, open]);

  useEffect(() => {
    return () => {
      if (attentionTimeoutRef.current !== null) {
        window.clearTimeout(attentionTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="jr-userswidget"
      data-open={open ? "1" : "0"}
      data-attention={attention ? "1" : "0"}
      data-placement={placement}
    >
      <div className="jr-userswidget-launchwrap" title="Session users">
        <JamroomIconButton
          iconId="jr-list"
          ariaLabel="Session users"
          pressed={open}
          onClick={() => {
            setAttention(false);
            setOpen((v) => !v);
          }}
          className="jr-userswidget-launch"
          size={44}
        />
        <span className="jr-badge jr-userswidget-count" title={`${connectedCount} connected / ${totalCount} total`}>
          {connectedCount}/{totalCount}
        </span>
      </div>

      <div className="jr-userswidget-drawer" role="dialog" aria-label="Session users" aria-hidden={!open}>
        <div className="jr-userswidget-header">
          <div className="jr-userswidget-title">Session users</div>
          <button
            type="button"
            className="jr-icon-btn jr-userswidget-close"
            onClick={() => setOpen(false)}
            title="Close"
            aria-label="Close users panel"
          >
            X
          </button>
        </div>

        <div className="jr-userswidget-body">
          <div className="jr-userswidget-card">
            <div className="jr-userswidget-cardtitle">Session time</div>
            <div className="jr-userswidget-row">
              <div className="jr-userswidget-k">Total</div>
              <div className="jr-userswidget-v">{timing.totalMs === null ? "?" : formatDurationMs(timing.totalMs)}</div>
            </div>
            <div className="jr-userswidget-row">
              <div className="jr-userswidget-k">Remaining</div>
              <div className={`jr-userswidget-v ${sessionExpired ? "is-expired" : ""}`}>
                {sessionExpired ? "Expired" : timing.remainingMs === null ? "?" : formatDurationMs(timing.remainingMs)}
              </div>
            </div>
            <div className="jr-userswidget-row">
              <div className="jr-userswidget-k">Expires</div>
              <div className="jr-userswidget-v">{timing.expiresText || "?"}</div>
            </div>

            <div className="jr-userswidget-extend">
              <label className="jr-userswidget-extendlabel">
                <span>Extend (hours)</span>
                <input
                  type="number"
                  min={1}
                  max={8}
                  step={1}
                  value={extendHours}
                  onChange={(e) => setExtendHours(Math.max(1, Math.min(8, Number(e.target.value || 1))))}
                  className="jr-userswidget-input"
                  aria-label="Hours to extend"
                  disabled={sessionExpired}
                />
              </label>
              <button
                type="button"
                className="jr-btn jr-btn--primary"
                disabled={sessionExpired}
                onClick={() => void onExtendSession(extendHours)}
              >
                Extend
              </button>
            </div>
          </div>

          <div className="jr-userswidget-card">
            <div className="jr-userswidget-cardtitle">Add user (pre-approve)</div>
            <div className="jr-userswidget-addrow">
              <input
                type="text"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="Name"
                className="jr-userswidget-input"
                aria-label="Add user name"
              />
              <button
                type="button"
                className="jr-btn jr-btn--success"
                disabled={!addName.trim()}
                onClick={() => {
                  const v = addName.trim();
                  if (!v) return;
                  setAddName("");
                  void onAction(v, "add");
                }}
              >
                Add
              </button>
            </div>
          </div>

          <div className="jr-userswidget-card">
            <div className="jr-userswidget-cardtitle">Users</div>
            {users.length === 0 ? (
              <div className="jr-userswidget-empty">No guests yet.</div>
            ) : (
              <div className="jr-userswidget-list">
                {users.map((u) => {
                  const isBusy = busyKey === u.guestNameNormalized;
                  const statusLabel =
                    u.status === "approved" ? "Approved" : u.status === "pending" ? "Pending" : "Removed";
                  const connLabel = u.connected ? "Connected" : "Disconnected";
                  const joinedAt = u.joinedAt ? new Date(u.joinedAt).toLocaleString() : null;
                  const lastSeen = u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : null;
                  const lastAct = u.lastActivityAt ? new Date(u.lastActivityAt).toLocaleString() : null;
                  const whenParts: string[] = [];
                  if (joinedAt) whenParts.push(`Joined: ${joinedAt}`);
                  if (lastAct) whenParts.push(`Last activity: ${lastAct}`);
                  else if (lastSeen) whenParts.push(`Last seen: ${lastSeen}`);
                  const whenText = whenParts.join(" · ");
                  return (
                    <div key={u.guestNameNormalized} className="jr-userswidget-item">
                      <div className="jr-userswidget-who">
                        <div className="jr-userswidget-name">
                          {u.displayName}
                          {u.isRecording ? <span className="jr-userswidget-rec">REC</span> : null}
                        </div>
                        <div className="jr-userswidget-meta">
                          <span className={`jr-userswidget-pill s-${u.status}`}>{statusLabel}</span>
                          <span className={`jr-userswidget-pill ${u.connected ? "ok" : "warn"}`}>{connLabel}</span>
                        </div>
                        <div className="jr-userswidget-when">
                          {whenText}
                        </div>
                      </div>

                      <div className="jr-userswidget-cta">
                        {u.status === "pending" && (
                          <button
                            type="button"
                            disabled={isBusy}
                            className="jr-btn jr-btn--success jr-btn--sm"
                            onClick={() => void onAction(u.displayName, "approve")}
                          >
                            Approve
                          </button>
                        )}
                        {u.status === "removed" && (
                          <button
                            type="button"
                            disabled={isBusy}
                            className="jr-btn jr-btn--success jr-btn--sm"
                            onClick={() => void onAction(u.displayName, "reapprove")}
                          >
                            Reapprove
                          </button>
                        )}
                        {u.status !== "removed" && (
                          <button
                            type="button"
                            disabled={isBusy}
                            className="jr-btn jr-btn--danger jr-btn--sm"
                            onClick={() => void onAction(u.displayName, "remove")}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionUsersWidget;
